import { IslandType } from '../blueprint-classes/islands/enums';

/**
 * Finish profiles centralize every property that varies by material family (currently
 * "standard", "mok", and "slat"). All non-MOK, non-slat surround materials share the
 * standard profile — they differ only in panel thickness, which is handled per-material by
 * `SURROUND_MATERIAL_THICKNESS` and cascades through `setSizeStandardPanelsCfn`.
 *
 * This file is the single source of truth for finish-family defaults. When a new
 * finish family is added with divergent base dimensions / overhangs / padding, add
 * one entry to `finishProfiles` — no other file should need family-specific branching.
 *
 * NOTE: the same raw overhang value can mean different things in different families
 * (mok's own standard/uncut overhang and standard's corner-trim-cut value are both 0.75";
 * standard's corner-trim value and slat's are 0.75"/0.5" but a user-set customization could
 * also legitimately sit at either number). `overhangRemapEnteringFrom` is therefore keyed by
 * the family being LEFT, not by value alone — a value is only ever reinterpreted once we know
 * exactly which family it came from, never guessed from the number in isolation. Directions
 * that stay genuinely ambiguous even with that context (e.g. a value that could equally be a
 * deliberate wall-flush customization) are simply omitted, so the value is left untouched
 * rather than guessed at.
 */

export type IslandDimensionDefault = { height: number, depth: number };

export type FinishFamilyKey = 'standard' | 'mok' | 'slat';

export type SurroundFinishProfile = {
	/** Registry key; used as the `material` string on `SurroundMaterial` for the MOK/slat families. Every other material maps to the 'standard' profile. */
	key: FinishFamilyKey;
	/** The standard (default) countertop overhang for this finish, in inches. MOK is 0.75"; standard and slat are both 2". */
	standardOverhang: number;
	/** Gap in inches left between the panel's bottom edge and a bottom-bound fridge. MOK reveals 0.75"; standard and slat sit flush. */
	fridgeBottomReveal: number;
	/** Minimum vertical gap in inches `panelEdgeLimit` requires between an item and the panel edge above or below it. Appliance-to-appliance clearance is not finish-aware. MOK allows 0.75"; standard and slat require 2.4375". */
	minVerticalEdgeGap: number;
	/** Default inter-island connection padding for this finish. */
	padding: number;
	/** Whether this finish ships separate corner trim hardware. MOKS is one molded shell, so it has none (GitHub #467). */
	hasCornerTrims: boolean;
	/** Height + depth defaults per island type. Only entries present are auto-snapped; missing types keep their current size. */
	islandDimensions: Readonly<Partial<Record<IslandType, Readonly<IslandDimensionDefault>>>>;
	/**
	 * Value-based overhang remap applied when a panel enters this finish, keyed by the family it
	 * is LEAVING (see file header). The inner map's key is the panel's current overhang; the value
	 * is what it becomes. An outgoing family with no entry here -- or a value not in its map --
	 * is left alone.
	 */
	overhangRemapEnteringFrom: Readonly<Partial<Record<FinishFamilyKey, ReadonlyMap<number, number>>>>;
};

export type CountertopFinishProfile = {
	key: 'standard' | 'mok';
	thickness: number;
};

// Slat has no size/overhang divergence from the standard grill/bar defaults — only its
// corner-trim value and padding differ — so it reuses these same island dimension presets.
// Frozen (including each entry) since the two families share this exact object by reference.
const STANDARD_ISLAND_DIMENSIONS: Readonly<Partial<Record<IslandType, Readonly<IslandDimensionDefault>>>> = Object.freeze({
	[IslandType.Grill]: Object.freeze({ height: 34.5, depth: 28 }),
	[IslandType.Bar]: Object.freeze({ height: 39.25, depth: 28 })
});

export const finishProfiles: Record<FinishFamilyKey, SurroundFinishProfile> = {
	standard: {
		key: 'standard',
		standardOverhang: 2,
		fridgeBottomReveal: 0,
		minVerticalEdgeGap: 2.4375,
		padding: 1,
		hasCornerTrims: true,
		islandDimensions: STANDARD_ISLAND_DIMENSIONS,
		overhangRemapEnteringFrom: {
			// Leaving MOK: its own uncut/seating baseline (0.75"/8") becomes standard's (2"/10").
			// Unambiguous -- these are MOK's own defining values, not customizations.
			mok: new Map([
				[0.75, 2],
				[8, 10]
			])
			// Leaving slat is intentionally absent: slat's corner-trim value (0.5") is a real
			// standard-family customization value too (see mok's own [0.5, 0] entry below), so a
			// 0.5" panel returning from slat cannot be told apart from a deliberate override made
			// before it ever visited slat. Left untouched rather than guessed at.
		}
	},
	mok: {
		key: 'mok',
		standardOverhang: 0.75,
		fridgeBottomReveal: 0.75,
		minVerticalEdgeGap: 0.75,
		padding: 0,
		hasCornerTrims: false,
		islandDimensions: {
			[IslandType.Grill]: { height: 36, depth: 28.5 },
			[IslandType.Bar]: { height: 40, depth: 21.25 }
		},
		overhangRemapEnteringFrom: {
			// Leaving standard: its uncut/seating baseline (2"/10") becomes MOK's (0.75"/8"), and its
			// own corner-trim/wall-flush values (0.75" or 0.5") collapse to MOK's flush 0".
			standard: new Map([
				[2, 0.75],
				[10, 8],
				[0.75, 0],
				[0.5, 0]
			])
			// Leaving slat is intentionally absent -- mok<->slat is out of scope for #397, and slat's
			// own corner-trim value (0.5") numerically collides with the standard-leaving [0.5, 0]
			// entry above in a way that can't be told apart without adding that scope.
		}
	},
	slat: {
		key: 'slat',
		standardOverhang: 2,
		fridgeBottomReveal: 0,
		minVerticalEdgeGap: 2.4375,
		padding: 0.5,
		hasCornerTrims: true,
		islandDimensions: STANDARD_ISLAND_DIMENSIONS,
		overhangRemapEnteringFrom: {
			// Leaving standard: its own corner-trim value (0.75") becomes slat's (0.5"). Unambiguous --
			// 0.75" is standard's defining corner-trim constant, not a value MOK panels normally carry.
			standard: new Map([
				[0.75, 0.5]
			])
			// Leaving MOK is intentionally absent: an ordinary UNCUT MOK panel sits at 0.75" (MOK's own
			// standardOverhang, see above) -- keying only on value, that 0.75" is indistinguishable from
			// standard's corner-trim cut, and would falsely mark an uncut MOK panel as corner-trimmed.
			// mok<->slat is out of scope for #397; the value is left untouched either way.
		}
	}
};

export const countertopFinishProfiles: Record<'standard' | 'mok', CountertopFinishProfile> = {
	standard: { key: 'standard', thickness: 1.25 },
	mok: { key: 'mok', thickness: 0.5 }
};

/**
 * Resolves the surround finish profile for a `SurroundMaterial.material` value.
 * Returns null when the input is null/undefined (initial load); everything else falls
 * back to the standard profile, so adding a new non-MOK, non-slat material requires no change here.
 */
export const getSurroundFinishProfile = (surroundMaterial: string | null | undefined): SurroundFinishProfile | null => {
	if (surroundMaterial === null || surroundMaterial === undefined) return null;
	if (surroundMaterial === 'mok') return finishProfiles.mok;
	if (surroundMaterial === 'slat') return finishProfiles.slat;
	return finishProfiles.standard;
};

/**
 * Resolves the countertop finish profile for a `CountertopMaterial.key` value.
 * MOK countertop keys use the `moks|` prefix; everything else is standard.
 */
export const getCountertopFinishProfile = (countertopKey: string | null | undefined): CountertopFinishProfile | null => {
	if (countertopKey === null || countertopKey === undefined) return null;
	if (countertopKey.startsWith('moks|')) return countertopFinishProfiles.mok;
	return countertopFinishProfiles.standard;
};

/**
 * Returns every known island-dimension default across all profiles for a given island type.
 * Used to gate the height/depth snap: only fire when the current size matches ANY known preset
 * (i.e., the user hasn't manually customized).
 */
export const getKnownIslandDimensionDefaults = (islandType: IslandType): Array<IslandDimensionDefault> => {
	const results: Array<IslandDimensionDefault> = [];
	Object.values(finishProfiles).forEach((profile) => {
		const dim = profile.islandDimensions[islandType];
		if (dim) results.push(dim);
	});
	return results;
};
